import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  FiSend, FiMapPin, FiCopy, FiCamera,
  FiInfo, FiAlertCircle, FiTool,
  FiUser, FiEyeOff, FiEye,
} from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui/Spinner';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { AnexoUpload } from '../../components/AnexoUpload';
import { EnderecoComMapa, ENDERECO_VAZIO, enderecoComoTexto, type EnderecoMapaValor } from '../../components/EnderecoComMapa';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  zeladoriaPortalService, TIPO_ZELADORIA_LABEL, TIPOS_ZELADORIA_LISTA,
  type TipoZeladoria, type AssuntoZeladoria,
} from '../../services/zeladoria.service';
import { solicitacoesService } from '../../services/solicitacoes.service';

const EXT_ANEXOS = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'doc', 'docx', 'xls', 'xlsx', 'txt'];
const MAX_ANEXOS = 5;

type Modo = 'identificada' | 'sigilosa' | 'anonima';

const MODO_INFO: Record<Modo, { titulo: string; descricao: string; icone: typeof FiUser }> = {
  identificada: {
    titulo: 'Identificada',
    descricao: 'Seu nome ficará visível para o setor responsável. Você receberá retorno e poderá anexar fotos.',
    icone: FiUser,
  },
  sigilosa: {
    titulo: 'Sigilosa',
    descricao: 'Sua identidade fica preservada — apenas a equipe autorizada poderá vê-la. Você recebe retorno.',
    icone: FiEyeOff,
  },
  anonima: {
    titulo: 'Anônima',
    descricao: 'Nenhum dado de identificação será enviado. Acompanhe apenas pelo link de protocolo.',
    icone: FiEye,
  },
};

export function ZeladoriaAbrirPage() {
  const { session, cidadao } = useAuth();

  const [modo, setModo] = useState<Modo>(session ? 'identificada' : 'anonima');
  const [tipo, setTipo] = useState<TipoZeladoria>('buraco_pavimento');
  const [assuntoId, setAssuntoId] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [loc, setLoc] = useState<EnderecoMapaValor>(ENDERECO_VAZIO);
  const [referencia, setReferencia] = useState('');
  const [contatoTelefone, setContatoTelefone] = useState('');
  const [fotos, setFotos] = useState<File[]>([]);

  const [assuntos, setAssuntos] = useState<AssuntoZeladoria[]>([]);
  const [carregandoAssuntos, setCarregandoAssuntos] = useState(true);

  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<{ numero: string; hash_publico: string } | null>(null);

  useEffect(() => {
    zeladoriaPortalService.listarAssuntos(getEstabelecimentoAtualId())
      .then((a) => {
        setAssuntos(a);
        if (a.length > 0) setAssuntoId(a[0].id);
      })
      .finally(() => setCarregandoAssuntos(false));
  }, []);

  // Sem login só é possível modo anônimo
  useEffect(() => {
    if (!session && modo !== 'anonima') setModo('anonima');
  }, [session, modo]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErro(null);

    if (titulo.trim().length < 3) { setErro('Informe um título.'); return; }
    if (descricao.trim().length < 10) { setErro('Detalhe a demanda (mínimo 10 caracteres).'); return; }
    if (!assuntoId) { setErro('Selecione uma categoria.'); return; }
    if (!loc.latitude && !enderecoComoTexto(loc)) {
      setErro('Informe a localização: use "Usar minha localização", clique no mapa ou preencha o endereço.');
      return;
    }

    setEnviando(true);
    try {
      const baseGeo = {
        endereco: enderecoComoTexto(loc) || undefined,
        cep: loc.cep.replace(/\D/g, '') || undefined,
        bairro: loc.bairro.trim() || undefined,
        latitude: loc.latitude ?? undefined,
        longitude: loc.longitude ?? undefined,
        referencia: referencia.trim() || undefined,
      };

      let docId = '';
      let r: { numero: string; hash_publico: string };

      if (modo !== 'anonima') {
        if (!cidadao) { setErro('Sessão expirada. Faça login novamente.'); setEnviando(false); return; }
        const r2 = await zeladoriaPortalService.abrirIdentificada({
          estabelecimento_id: getEstabelecimentoAtualId(),
          assunto_id: assuntoId,
          tipo_zeladoria: tipo,
          titulo: titulo.trim(),
          descricao: descricao.trim(),
          cidadao_id: cidadao.id,
          sigilosa: modo === 'sigilosa',
          ...baseGeo,
        });
        r = { numero: r2.numero, hash_publico: r2.hash_publico };
        docId = r2.id;

        // Upload de fotos para cidadao logado
        if (fotos.length > 0) {
          for (const f of fotos) {
            await solicitacoesService.uploadAnexo({
              documentoId: docId,
              estabelecimentoId: getEstabelecimentoAtualId(),
              cidadaoId: cidadao.id,
              cidadaoNome: cidadao.nome,
              arquivo: f,
              categoria: 'foto',
            });
          }
        }
        // E-mail de confirmação de abertura (best-effort).
        try { await supabase.functions.invoke('notificar-cidadao-protocolo', { body: { documento_id: docId, evento: 'criado' } }); } catch { /* não bloqueia */ }
      } else {
        r = await zeladoriaPortalService.abrirAnonima({
          estabelecimento_id: getEstabelecimentoAtualId(),
          assunto_id: assuntoId,
          tipo_zeladoria: tipo,
          titulo: titulo.trim(),
          descricao: descricao.trim(),
          contato_telefone: contatoTelefone.trim() || undefined,
          ...baseGeo,
        });
      }

      setResultado(r);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível registrar a demanda.');
    } finally {
      setEnviando(false);
    }
  }

  // Tela de sucesso
  if (resultado) {
    const linkAcompanhamento = `${window.location.origin}/protocolo/${resultado.numero}/${resultado.hash_publico}`;
    return (
      <PainelResultado
        titulo="Demanda registrada!"
        descricao={<>Número: <strong className="tabular-nums">{resultado.numero}</strong></>}
        acoes={
          <>
            <Link
              to={`/protocolo/${resultado.numero}/${resultado.hash_publico}`}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-medium bg-institutional text-white hover:bg-institutional-dark transition"
            >
              Ver acompanhamento
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-medium border border-slate-300 bg-white text-slate-900 hover:bg-slate-50 transition"
            >
              Voltar ao início
            </Link>
          </>
        }
      >
        {modo === 'anonima' && (
          <Alert variante="aviso" className="mb-4">
            <strong>Guarde este link de acompanhamento.</strong> É a única forma de você consultar o andamento (a demanda foi registrada anonimamente).
          </Alert>
        )}
        <div className="bg-slate-100 border border-slate-200 rounded p-3 flex items-center gap-2 text-sm">
          <input type="text" readOnly value={linkAcompanhamento} className="flex-1 bg-transparent border-0 outline-none text-xs" />
          <button
            onClick={() => navigator.clipboard.writeText(linkAcompanhamento)}
            className="text-slate-600 hover:text-institutional p-1"
            title="Copiar"
          >
            <FiCopy size={14} />
          </button>
        </div>
      </PainelResultado>
    );
  }

  return (
    <PortalPage
      titulo="Zeladoria urbana"
      subtitulo="Reporte buracos, falta de iluminação, problemas com lixo, árvores caídas e demais demandas urbanas."
      icone={<FiTool size={26} />}
      voltarHref="/"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Modo de identificação */}
        <SectionCard
          titulo="Como você quer se identificar?"
          subtitulo="A escolha afeta quem vê seus dados e se você receberá retorno."
          icone={<FiUser size={18} />}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(Object.keys(MODO_INFO) as Modo[]).map((m) => {
              const ativo = modo === m;
              const desabilitado = !session && m !== 'anonima';
              const { titulo: t, descricao: d, icone: Icone } = MODO_INFO[m];
              return (
                <button
                  key={m}
                  type="button"
                  disabled={desabilitado}
                  onClick={() => setModo(m)}
                  className={`text-left p-4 rounded border-2 transition ${
                    ativo
                      ? 'border-institutional bg-institutional/5'
                      : desabilitado
                        ? 'border-slate-200 opacity-50 cursor-not-allowed'
                        : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Icone className="text-institutional mb-2" size={18} />
                  <div className="font-medium text-slate-900 text-sm">{t}</div>
                  <p className="text-xs text-slate-600 mt-1 leading-snug">{d}</p>
                  {desabilitado && (
                    <p className="text-[11px] text-amber-700 mt-2">Requer login.</p>
                  )}
                </button>
              );
            })}
          </div>
          {!session && (
            <div className="mt-3 text-xs text-slate-500">
              <Link to="/login" className="text-institutional underline">Entrar</Link>{' '}
              para usar demanda identificada ou sigilosa, anexar fotos e receber retorno.
            </div>
          )}
        </SectionCard>

        {/* Tipo + categoria */}
        <SectionCard titulo="Sobre o problema" icone={<FiTool size={18} />}>
          <div className="space-y-3">
            <div>
              <Label htmlFor="tipo" obrigatorio>Tipo de problema</Label>
              <select
                id="tipo"
                value={tipo}
                onChange={(e) => setTipo(e.target.value as TipoZeladoria)}
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
              >
                {TIPOS_ZELADORIA_LISTA.map((t) => (
                  <option key={t} value={t}>{TIPO_ZELADORIA_LABEL[t]}</option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="assunto" obrigatorio>Categoria de atendimento</Label>
              {carregandoAssuntos ? (
                <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner tamanho="sm" /> Carregando...</div>
              ) : assuntos.length === 0 ? (
                <Alert variante="erro">A prefeitura ainda não cadastrou categorias de zeladoria.</Alert>
              ) : (
                <select
                  id="assunto"
                  value={assuntoId}
                  onChange={(e) => setAssuntoId(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                >
                  {assuntos.map((a) => (
                    <option key={a.id} value={a.id}>{a.nome}</option>
                  ))}
                </select>
              )}
              <p className="text-xs text-slate-500 mt-1">A categoria define qual setor da prefeitura vai atender.</p>
            </div>

            <div>
              <Label htmlFor="titulo" obrigatorio>Título</Label>
              <Input
                id="titulo"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder={`Ex: ${TIPO_ZELADORIA_LABEL[tipo]} na rua X`}
                required
                minLength={3}
              />
            </div>

            <div>
              <Label htmlFor="descricao" obrigatorio>Descrição detalhada</Label>
              <textarea
                id="descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                placeholder="Descreva o problema com detalhes..."
                required
                minLength={10}
              />
            </div>
          </div>
        </SectionCard>

        {/* Localização */}
        <SectionCard
          titulo="Onde fica o problema?"
          subtitulo="Use sua localização, clique no mapa ou informe o CEP/endereço (preenche e marca o pin)."
          icone={<FiMapPin size={18} />}
        >
          <EnderecoComMapa valor={loc} onChange={setLoc} />

          <div className="mt-3">
            <Label htmlFor="ref">Ponto de referência</Label>
            <Input
              id="ref"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="Ex: em frente à padaria"
            />
          </div>
        </SectionCard>

        {/* Anexos (identificada/sigilosa) ou contato (anônimo) */}
        {modo !== 'anonima' ? (
          <SectionCard titulo="Anexos (opcional)" icone={<FiCamera size={18} />}>
            <p className="text-xs text-slate-600 mb-2">
              Fotos do problema, documentos ou comprovantes. Até {MAX_ANEXOS} arquivos, 10 MB cada.
            </p>
            <AnexoUpload
              arquivos={fotos}
              onChange={setFotos}
              extensoesPermitidas={EXT_ANEXOS}
              tamanhoMaxMB={10}
              multiplo
              maxArquivos={MAX_ANEXOS}
              legenda={`Até ${MAX_ANEXOS} arquivos · ${EXT_ANEXOS.join(', ').toUpperCase()}`}
            />
          </SectionCard>
        ) : (
          <SectionCard titulo="Contato (opcional)" icone={<FiInfo size={18} />}>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900 flex items-start gap-2 mb-3">
              <FiInfo className="flex-shrink-0 mt-0.5" size={14} />
              <span>
                Para anexar fotos, é preciso estar logado. <Link to="/login" className="underline font-medium">Fazer login</Link> ou <Link to="/cadastro" className="underline font-medium">cadastrar-se</Link>. Se preferir continuar anônimo, deixe um telefone para contato (opcional).
              </span>
            </div>
            <div>
              <Label htmlFor="contato">Telefone para contato (opcional)</Label>
              <Input
                id="contato"
                value={contatoTelefone}
                onChange={(e) => setContatoTelefone(e.target.value)}
                placeholder="(00) 00000-0000"
              />
              <p className="text-xs text-slate-500 mt-1">
                Se a equipe precisar de mais detalhes, poderá entrar em contato. Não é divulgado publicamente.
              </p>
            </div>
          </SectionCard>
        )}

        {modo === 'sigilosa' && (
          <Alert variante="info">
            Sua identidade é preservada — apenas a equipe autorizada da prefeitura terá acesso aos
            seus dados nesta demanda.
          </Alert>
        )}

        {erro && (
          <Alert variante="erro" className="flex items-start gap-2">
            <FiAlertCircle className="flex-shrink-0 mt-0.5" size={14} />
            <span>{erro}</span>
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Link
            to="/"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-medium border border-slate-300 bg-white text-slate-900 hover:bg-slate-50 transition"
          >
            Cancelar
          </Link>
          <Button type="submit" loading={enviando}>
            <FiSend size={14} /> Enviar demanda
          </Button>
        </div>
      </form>
    </PortalPage>
  );
}
